/**
 * ONE adapter for the shared `ProfileNotificationsSection`.
 *
 * Renders the two ONE-specific push preference toggles the backend defines:
 * `toggle_one_reunification_push_notification` (covers the
 * reunification pickup AND pickup-delegation categories — the backend maps
 * both onto the `one_reunification` preference key) and
 * `toggle_one_student_checkin_push_notification`.
 *
 * The shared section is presentational; this wrapper owns the ONE-specific
 * state + persistence (same shape as SMS's `ProfileNotifications`
 * adapter): values seed from a self `GET /api/v2/user/{id}/`, toggles PATCH
 * the same resource optimistically via the typed client, reverting with an
 * error toast on failure. Defaults are opted-IN — a missing field
 * reads as `true`.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ProfileNotificationsSection,
  type ProfileNotificationItem,
} from '@tiaki/profile-ui';
import { useUser, type AllauthUser } from '@aegix/auth-identity';
import { client, unwrap } from '@/lib/http-client';
import { toaster } from '@/components/ui/toaster';

const ONE_PREF_KEYS = [
  'toggle_one_reunification_push_notification',
  'toggle_one_student_checkin_push_notification',
] as const;

type OnePrefKey = (typeof ONE_PREF_KEYS)[number];

type OnePrefValues = Record<OnePrefKey, boolean>;

/** ONE preference defaults are opted-IN. */
const DEFAULT_VALUES: OnePrefValues = {
  toggle_one_reunification_push_notification: true,
  toggle_one_student_checkin_push_notification: true,
};

/**
 * Computed single-key literals keep the PATCH body assignable to the typed
 * `PatchedUserRequest` (a computed `{ [key]: value }` widens to an index
 * signature TS rejects).
 */
function prefPatchBody(key: OnePrefKey, value: boolean) {
  return key === 'toggle_one_reunification_push_notification'
    ? { toggle_one_reunification_push_notification: value }
    : { toggle_one_student_checkin_push_notification: value };
}

export function OneNotificationPreferences() {
  const { t } = useTranslation();
  const user = useUser<AllauthUser>();
  // The lib's user id can surface as string|number; the typed user
  // endpoints take a numeric path param.
  const userId = user ? Number(user.id) : undefined;

  const [values, setValues] = useState<OnePrefValues>(DEFAULT_VALUES);

  const { data: userDetail } = useQuery({
    queryKey: ['oneUserPushPreferences', userId],
    enabled: !!userId,
    queryFn: async () =>
      unwrap(
        await client.GET('/api/v2/user/{id}/', {
          params: { path: { id: userId as number } },
        }),
      ),
  });

  useEffect(() => {
    if (!userDetail) return;
    setValues({
      toggle_one_reunification_push_notification:
        userDetail.toggle_one_reunification_push_notification ?? true,
      toggle_one_student_checkin_push_notification:
        userDetail.toggle_one_student_checkin_push_notification ?? true,
    });
  }, [userDetail]);

  const items: ProfileNotificationItem[] = [
    {
      id: 'toggle_one_reunification_push_notification',
      label: t(
        'toggle_one_reunification_push_notification',
        'Reunification pickup & delegation',
      ),
      editable: true,
    },
    {
      id: 'toggle_one_student_checkin_push_notification',
      label: t(
        'toggle_one_student_checkin_push_notification',
        'Student check-in',
      ),
      editable: true,
    },
  ];

  const handleToggle = async (id: string, next: boolean) => {
    const key = id as OnePrefKey;
    if (!userId || !ONE_PREF_KEYS.includes(key)) return;
    setValues((prev) => ({ ...prev, [key]: next }));
    try {
      unwrap(
        await client.PATCH('/api/v2/user/{id}/', {
          params: { path: { id: userId } },
          body: prefPatchBody(key, next),
        }),
      );
      toaster.create({
        title: t(
          'profile.pushPrefUpdateSuccess',
          'Notification settings updated',
        ),
        type: 'success',
        closable: true,
      });
    } catch {
      setValues((prev) => ({ ...prev, [key]: !next }));
      toaster.create({
        title: t(
          'profile.pushPrefUpdateError',
          'Failed to update notification settings',
        ),
        type: 'error',
        closable: true,
      });
    }
  };

  return (
    <ProfileNotificationsSection
      items={items}
      values={values}
      onToggle={handleToggle}
      title={t('profile.pushNotifications', 'Push notifications')}
      e2eTitle="one-push-preferences"
    />
  );
}
